import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Activity,
  CalendarDays,
  Download,
  Loader2,
  LogOut,
  MapPin,
  RefreshCw,
  Search,
  ShieldCheck,
  TrendingUp,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "gba_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) {
    return (
      <LoginScreen
        onAuthenticated={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }
  return <Dashboard token={token} onLogout={handleLogout} />;
}

/* -------------------- Login -------------------- */
function LoginScreen({ onAuthenticated }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/admin/login`, {
        username: username.trim(),
        password,
      });
      onAuthenticated(r.data.token);
      toast.success("Logged in");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      setError(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F0EA] flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-white border-2 border-[#0A0A0A] rounded-2xl p-7 neo-shadow-lg"
      >
        <div className="flex items-center gap-3 mb-3">
          <img
            src="/brand/logo.png"
            alt="Ganapathi Badminton Academy"
            className="w-10 h-10 rounded-lg bg-white border-2 border-[#0A0A0A] p-0.5 object-contain"
          />
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} />
            <p className="text-[11px] font-display font-extrabold uppercase tracking-[0.18em]">
              Coach Login
            </p>
          </div>
        </div>
        <h1 className="font-display font-black uppercase leading-[0.95] tracking-tighter text-3xl sm:text-4xl">
          Ganapathi<br />
          Dashboard.
        </h1>
        <p className="text-sm mt-2 text-neutral-600 font-medium">
          Sign in to manage signups and download reports.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="block text-[11px] font-display font-extrabold uppercase tracking-[0.12em] mb-2">
              Username
            </label>
            <input
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              autoComplete="username"
              className="w-full bg-white text-[#0A0A0A] font-medium text-base p-3.5 border-2 border-[#0A0A0A] rounded-xl focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-display font-extrabold uppercase tracking-[0.12em] mb-2">
              Password
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              className="w-full bg-white text-[#0A0A0A] font-medium text-base p-3.5 border-2 border-[#0A0A0A] rounded-xl focus:outline-none"
            />
          </div>

          {error && (
            <p
              data-testid="admin-login-error"
              className="text-xs font-bold bg-red-100 border-2 border-red-600 text-red-700 rounded-xl px-3 py-2"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={!username || !password || loading}
            className="w-full bg-[#D4FF00] text-[#0A0A0A] font-display font-black text-base py-3.5 px-6 border-2 border-[#0A0A0A] rounded-xl neo-shadow neo-shadow-hover uppercase flex justify-center items-center gap-2 disabled:opacity-40"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
            Sign in
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------------------- Dashboard -------------------- */
function Dashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [skillFilter, setSkillFilter] = useState("All");
  const [slotFilter, setSlotFilter] = useState("All");
  const [sortBy, setSortBy] = useState("latest"); // latest | earliest | slot-asc | slot-desc | name
  const [exporting, setExporting] = useState(false);

  const SLOT_ORDER = [
    "8:00 AM – 10:00 AM",
    "10:00 AM – 12:00 PM",
    "12:00 PM – 4:00 PM",
    "4:00 PM – 6:00 PM",
    "6:00 PM – 8:00 PM",
  ];

  const authHeader = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeader }),
        axios.get(`${API}/admin/signups`, { headers: authHeader }),
      ]);
      setStats(s.data);
      setSignups(l.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, l] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: authHeader }),
          axios.get(`${API}/admin/signups`, { headers: authHeader }),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setSignups(l.data || []);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          toast.error("Session expired");
          onLogout();
        } else {
          toast.error("Failed to load");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, authHeader, onLogout]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/admin/signups/export.xlsx`, {
        headers: authHeader,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ganapathi-signups-${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Export failed");
      }
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = signups.filter((s) => {
      if (skillFilter !== "All" && s.skill_level !== skillFilter) return false;
      if (slotFilter !== "All" && s.preferred_slot !== slotFilter) return false;
      if (!q) return true;
      return (
        s.name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.whatsapp_number?.includes(q) ||
        s.area?.toLowerCase().includes(q) ||
        s.preferred_slot?.toLowerCase().includes(q)
      );
    });

    const slotIdx = (s) => {
      const i = SLOT_ORDER.indexOf(s?.preferred_slot);
      return i === -1 ? 999 : i;
    };
    const created = (s) => new Date(s?.created_at || 0).getTime();

    switch (sortBy) {
      case "earliest":
        list = [...list].sort((a, b) => created(a) - created(b));
        break;
      case "slot-asc":
        list = [...list].sort(
          (a, b) => slotIdx(a) - slotIdx(b) || created(b) - created(a)
        );
        break;
      case "slot-desc":
        list = [...list].sort(
          (a, b) => slotIdx(b) - slotIdx(a) || created(b) - created(a)
        );
        break;
      case "name":
        list = [...list].sort((a, b) =>
          (a?.name || "").localeCompare(b?.name || "")
        );
        break;
      case "latest":
      default:
        list = [...list].sort((a, b) => created(b) - created(a));
        break;
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signups, query, skillFilter, slotFilter, sortBy]);

  return (
    <div className="min-h-screen bg-[#F4F0EA] pb-12" data-testid="admin-page">
      {/* Header */}
      <div className="border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img
              src="/brand/logo.png"
              alt="Ganapathi Badminton Academy"
              className="w-11 h-11 rounded-lg bg-white p-0.5 object-contain"
            />
            <div>
              <p className="text-[10px] font-display font-extrabold uppercase tracking-[0.2em] text-[#D4FF00]">
                Ganapathi Badminton · Tumkur
              </p>
              <h1 className="font-display font-black uppercase tracking-tighter text-xl sm:text-2xl mt-0.5">
                Coach Dashboard
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="bg-white text-[#0A0A0A] border-2 border-white rounded-xl px-3 py-2 font-display font-extrabold uppercase text-xs flex items-center gap-1 hover:bg-[#D4FF00] transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="bg-transparent text-white border-2 border-white rounded-xl px-3 py-2 font-display font-extrabold uppercase text-xs flex items-center gap-1 hover:bg-white hover:text-[#0A0A0A] transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-6">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KpiCard
            testid="kpi-total"
            label="Total signups"
            value={stats?.total ?? "—"}
            icon={<Users size={18} />}
            color="#D4FF00"
          />
          <KpiCard
            testid="kpi-today"
            label="Today"
            value={stats?.today ?? "—"}
            icon={<Activity size={18} />}
            color="#FF90E8"
          />
          <KpiCard
            testid="kpi-week"
            label="This week"
            value={stats?.this_week ?? "—"}
            icon={<TrendingUp size={18} />}
            color="#0051FF"
            textColor="#fff"
          />
          <KpiCard
            testid="kpi-areas"
            label="Active areas"
            value={stats?.by_area?.length ?? "—"}
            icon={<MapPin size={18} />}
            color="#FFFFFF"
          />
        </div>

        {/* Breakdown row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <BreakdownCard
            testid="slot-breakdown"
            title="By slot · 27 Sep"
            items={(stats?.by_slot || []).map((s) => ({
              label: s.slot,
              count: s.count,
            }))}
            total={stats?.total || 0}
            accent="#FF90E8"
          />
          <BreakdownCard
            testid="skill-breakdown"
            title="By skill level"
            items={
              stats
                ? Object.entries(stats.by_skill || {}).map(([k, v]) => ({
                    label: k,
                    count: v,
                  }))
                : []
            }
            total={stats?.total || 0}
          />
          <BreakdownCard
            testid="area-breakdown"
            title="By area"
            items={(stats?.by_area || []).map((a) => ({
              label: a.area,
              count: a.count,
            }))}
            total={stats?.total || 0}
          />
        </div>

        {/* Toolbar */}
        <div className="mt-7 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 flex-1">
            <div className="flex items-stretch bg-white border-2 border-[#0A0A0A] rounded-xl overflow-hidden neo-shadow flex-1 min-w-[220px] max-w-md">
              <div className="px-3 flex items-center bg-[#D4FF00] border-r-2 border-[#0A0A0A]">
                <Search size={16} />
              </div>
              <input
                data-testid="signup-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, number, slot, area"
                className="flex-1 px-3 py-2.5 text-sm font-medium focus:outline-none"
              />
            </div>
            <select
              data-testid="skill-filter"
              value={skillFilter}
              onChange={(e) => setSkillFilter(e.target.value)}
              className="bg-white border-2 border-[#0A0A0A] rounded-xl px-3 py-2.5 text-sm font-bold neo-shadow"
            >
              <option value="All">All skills</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Pro">Pro</option>
            </select>
            <select
              data-testid="slot-filter"
              value={slotFilter}
              onChange={(e) => setSlotFilter(e.target.value)}
              className="bg-white border-2 border-[#0A0A0A] rounded-xl px-3 py-2.5 text-sm font-bold neo-shadow"
            >
              <option value="All">All slots</option>
              {SLOT_ORDER.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              data-testid="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white border-2 border-[#0A0A0A] rounded-xl px-3 py-2.5 text-sm font-bold neo-shadow"
            >
              <option value="latest">Sort: Latest first</option>
              <option value="earliest">Sort: Earliest first</option>
              <option value="slot-asc">Sort: Slot early → late</option>
              <option value="slot-desc">Sort: Slot late → early</option>
              <option value="name">Sort: Name A → Z</option>
            </select>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || signups.length === 0}
            data-testid="export-xlsx-button"
            className="bg-[#0051FF] text-white border-2 border-[#0A0A0A] rounded-xl px-4 py-2.5 font-display font-extrabold uppercase text-sm neo-shadow neo-shadow-hover flex items-center gap-2 disabled:opacity-40"
          >
            {exporting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Download Excel
          </button>
        </div>

        {/* Table */}
        <div className="mt-4 bg-white border-2 border-[#0A0A0A] rounded-2xl neo-shadow overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center gap-2 text-sm font-bold">
              <Loader2 className="animate-spin" size={16} /> Loading signups…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="font-display font-extrabold uppercase">
                {signups.length === 0
                  ? "No signups yet"
                  : "No signups match your filters"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="admin-signup-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0A0A0A] text-[#D4FF00]">
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>WhatsApp</Th>
                    <Th>Age</Th>
                    <Th>Slot</Th>
                    <Th>Skill</Th>
                    <Th>Area</Th>
                    <Th>Joined</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => (
                    <tr
                      key={s.id}
                      data-testid={`admin-signup-${s.id}`}
                      className={i % 2 === 0 ? "bg-white" : "bg-[#FAF8F4]"}
                    >
                      <Td className="font-display font-extrabold whitespace-nowrap">
                        {s.name}
                      </Td>
                      <Td className="text-neutral-700 break-all">
                        {s.email || "—"}
                      </Td>
                      <Td className="whitespace-nowrap font-mono">
                        +{s.whatsapp_number}
                      </Td>
                      <Td className="whitespace-nowrap font-bold">
                        {s.age ?? "—"}
                      </Td>
                      <Td className="whitespace-nowrap text-xs font-bold">
                        {s.preferred_slot || "—"}
                      </Td>
                      <Td>
                        <SkillPill level={s.skill_level} />
                      </Td>
                      <Td className="whitespace-nowrap">{s.area}</Td>
                      <Td className="whitespace-nowrap text-neutral-500 text-xs">
                        {new Date(s.created_at).toLocaleString()}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[11px] mt-3 text-neutral-500 font-medium">
          <CalendarDays size={12} className="inline mr-1" />
          Showing {filtered.length} of {signups.length} signups · token expires
          in 12h
        </p>
      </div>
    </div>
  );
}

/* -------------------- Bits -------------------- */
function KpiCard({ testid, label, value, icon, color, textColor = "#0A0A0A" }) {
  return (
    <div
      data-testid={testid}
      className="border-2 border-[#0A0A0A] rounded-2xl p-4 neo-shadow"
      style={{ background: color, color: textColor }}
    >
      <div className="flex items-center gap-2 opacity-80">
        {icon}
        <p className="text-[10px] font-display font-extrabold uppercase tracking-[0.15em]">
          {label}
        </p>
      </div>
      <p className="font-display font-black text-4xl mt-2 tracking-tighter">
        {value}
      </p>
    </div>
  );
}

function BreakdownCard({ testid, title, items, total, accent = "#D4FF00" }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border-2 border-[#0A0A0A] rounded-2xl p-5 neo-shadow"
    >
      <p className="text-[10px] font-display font-extrabold uppercase tracking-[0.15em] mb-3 text-neutral-500">
        {title}
      </p>
      <div className="space-y-2.5">
        {items.length === 0 && (
          <p className="text-xs text-neutral-400 font-medium">No data yet.</p>
        )}
        {items.map((it) => {
          const pct = total > 0 ? Math.round((it.count / total) * 100) : 0;
          return (
            <div key={it.label}>
              <div className="flex items-baseline justify-between text-xs font-bold">
                <span className="truncate pr-2">{it.label}</span>
                <span className="text-neutral-500 whitespace-nowrap">
                  {it.count} · {pct}%
                </span>
              </div>
              <div className="mt-1 h-2 bg-neutral-100 rounded-full overflow-hidden border border-[#0A0A0A]">
                <div
                  className="h-full"
                  style={{ width: `${pct}%`, background: accent }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left px-4 py-3 text-[10px] font-display font-extrabold uppercase tracking-[0.15em]">
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-3 align-top border-t border-neutral-200 ${className}`}>
      {children}
    </td>
  );
}

function SkillPill({ level }) {
  const colors = {
    Beginner: "#D4FF00",
    Intermediate: "#FF90E8",
    Advanced: "#0051FF",
    Pro: "#0A0A0A",
  };
  const text = level === "Pro" || level === "Advanced" ? "#fff" : "#0A0A0A";
  return (
    <span
      className="inline-block border-2 border-[#0A0A0A] rounded-full px-2.5 py-0.5 text-[10px] font-display font-extrabold uppercase tracking-wider"
      style={{ background: colors[level] || "#FFF", color: text }}
    >
      {level}
    </span>
  );
}
